import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  IconButton,
  Tooltip,
  // InputBase went with the non-functional search box.
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Language as WebsiteIcon,
  Payment as CreditIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  SupportAgent as SupportIcon,
  // SearchIcon went with the search box; NotificationsIcon now lives inside
  // NotificationsMenu, which owns the bell.
  // TrendingUp as TrendingUpIcon, // only used by the commented-out Avg Rank widget below
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  // Bolt as BoltIcon, // only used by the removed Quick Start Guide 3-step card
  School as TutorialIcon,
  BarChart as BarChartIcon,
  InfoOutlined as InfoOutlinedIcon,
  Refresh as RefreshIcon,
  PlayCircleOutline as PlayCircleIcon,
  ConfirmationNumberOutlined as TicketIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import WebsiteManagement from './WebsiteManagement';
import CreditManagement from './CreditManagement';
import AnalyticsDashboard from './AnalyticsDashboard';
import Settings from './Settings';
import SupportTickets from './SupportTickets';
import Tutorial from './Tutorial';
import { apiService } from '../services/api';
import { getPlanDisplayName } from '../utils/planUtils';
// CREDIT_TYPE_LABELS is no longer read here — the Overview credits card is back
// to a single total (see the commented-out breakdown below).
import { useCreditBalances /*, CREDIT_TYPE_LABELS */ } from '../hooks/useCreditBalances';
import { creditsSummaryMessage } from '../utils/creditRates';
import { usePieceEstimate } from '../hooks/usePieceEstimate';
import { isWebsiteConnected, websiteStatusLabel } from '../utils/websiteStatus';
import {
  EMPTY_GROWTH, buildContentGrowthFromAggregated, growthLabelFor, relativeTime,
  GROWTH_WINDOW_MONTHS,
} from '../utils/contentGrowth';
import { useWebsitesWithStatus } from '../hooks/useWebsitesWithStatus';
import CreditExpiryBanner from './CreditExpiryBanner';
import { greetingFor, heroSubtitle, hasSeenDashboard, markDashboardSeen } from '../utils/greeting';
import NotificationsMenu from './NotificationsMenu';
import { useNotifications } from '../hooks/useNotifications';
import SuccessDialog from './SuccessDialog';
import {
  creditPurchaseConfirmedMessage, CREDIT_PURCHASE_CONFIRMED_TITLE, isCreditsPurchaseRedirect,
  subscriptionStartedMessage, SUBSCRIPTION_STARTED_TITLE,
  paymentConfirmedMessage, PAYMENT_CONFIRMED_TITLE,
} from '../utils/subscriptionCopy';

// "Tutorial" was renamed to "Quick Start Guide" (nav item, page title AND
// route). Paths live in utils/routes.js so sibling pages can link to each other
// without importing Dashboard (which renders them) and forming a module cycle.
import {
  QUICK_START_PATH,
  // PUBLIC_TUTORIALS_PATH is no longer linked from Overview (see the "New here?"
  // card below). The /tutorials route itself stays — it is the shareable,
  // no-login copy of the guide and is still worth having.
  SUPPORT_PATH,
  OVERVIEW_PATH,
  WEBSITES_PATH,
  CREDITS_PATH,
  PLANS_PATH,
  NEW_TICKET_PATH,
} from '../utils/routes';
// The header's height, shared with the sections that scroll themselves clear of
// it — see APP_HEADER_HEIGHT.
import { APP_HEADER_HEIGHT } from '../utils/layout';

// ── colour tokens from design ──────────────────────────────────────────────
const C = {
  pageBg: '#F8FAFA',
  sidebarBg: '#F8FAFC',
  sidebarBorder: '#E2E8F0',
  headerBg: 'rgba(255,255,255,0.85)',
  text: '#0F172A',
  textMuted: '#64748B',
  white: '#FFFFFF',
  creditsCard: '#156874',
  warmCard: '#F4F1DE',
  quickStartCard: 'rgba(244,241,222,0.55)',
  stepBg: '#F2F4F4',
  stepBorder: 'rgba(0,0,0,0)',
  activeStatus: { bg: 'rgba(0,106,99,0.10)', text: '#006A63' },
  syncStatus: { bg: 'rgba(167,57,30,0.10)', text: '#A7391E' },
  accentTeal: '#1C7C73',
  accentOrange: '#FF7A59',
  rankIcon: 'rgba(72,178,168,0.10)',
  gradientOrange: 'linear-gradient(45deg, #A7391E 0%, #FF7A59 100%)',
  iconFg: '#E2E8F0',
  websiteBorder: '#B8A1A1',
};

// Superseded by buildContentGrowthFromAggregated (fixed 5-month window → adaptive
// window anchored on the user's first content generation, plus an empty
// state). Kept here, commented out, per the "don't delete" convention.
// const buildLast5MonthGrowth = (generations = []) => {
//   const now = new Date();
//   const months = [];
//   for (let i = 4; i >= 0; i--) {
//     const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
//     months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleDateString('en-US', { month: 'short' }), value: 0 });
//   }
//   const byKey = Object.fromEntries(months.map((m) => [m.key, m]));
//   generations.forEach((g) => {
//     if (g.credit_type && g.credit_type !== 'content') return;
//     const created = g.created_at || g.createdAt;
//     if (!created) return;
//     const d = new Date(created);
//     if (Number.isNaN(d.getTime())) return;
//     const bucket = byKey[`${d.getFullYear()}-${d.getMonth()}`];
//     if (bucket) bucket.value += 1;
//   });
//   return months;
// };

// The window arithmetic now lives in utils/contentGrowth.js, with tests. It was
// module-private here, which is why the "one post does not show up" case had
// nothing pinning it. Behaviour is unchanged; the builders just take an
// injectable `now` so the window can be tested at a fixed date.

// How often Overview re-reads content growth. Articles are generated in the
// WordPress plugin, entirely outside this app, so there is no event to listen
// for — polling is the only way the CMS finds out. 60s is well under the
// "did it work?" patience threshold while staying cheap: one indexed read per
// minute per open tab, and only while the tab is actually visible.
const GROWTH_POLL_MS = 60000;

// ── Chart geometry ──────────────────────────────────────────────────────────
// The tallest bar's height in pixels, and the floor a NON-ZERO month is never
// drawn below. Without that floor a small month next to a large one collapses
// to a few pixels: at the real numbers behind the reported chart — 120 in July,
// 6 in August — August rendered 6px tall and read as nothing at all.
// GROWTH_ZERO_BAR_PX is the stub a genuinely empty month keeps, so the axis
// shows five columns rather than three bars and two gaps.
const GROWTH_PLOT_PX = 120;
const GROWTH_MIN_BAR_PX = 10;
const GROWTH_ZERO_BAR_PX = 3;


// ── Overview Tab ─────────────────────────────────────────────────────────────
const OverviewTab = ({ userData, onNavigate }) => {
  // Shared with the Websites page's "Credits Remaining" card (useCreditBalances)
  // so both always show the same live credit_balances numbers.
  // One pooled balance now — the per-type breakdown went with the pool merge.
  // `lots` comes from the same call the credit tiles already make, so the
  // expiry banner costs no extra request. A self-contained banner calling the
  // hook itself would refetch on every mount.
  const {
    total: totalCredits, pricing, lots,
  } = useCreditBalances(userData?.id);
  // "≈ N pieces" — from the server's one estimate endpoint, shared with the plugin.
  const contentPieces = usePieceEstimate(totalCredits);

  // ── "Welcome" vs "Welcome back" ─────────────────────────────────────────
  // Read ONCE per mount, before the effect below records the visit. Reading it
  // on every render would flip a first-time greeting to "Welcome back" the
  // moment anything else on this page re-rendered — which, on a dashboard that
  // is loading credits, websites and growth data, is immediately.
  //
  // App.js does not render this until auth has resolved, so `userData` is
  // already in hand on the first render and the lazy initialiser sees the real
  // id rather than undefined.
  const [returning] = useState(() => hasSeenDashboard(userData?.id));

  useEffect(() => {
    markDashboardSeen(userData?.id);
  }, [userData?.id]);
  const [rawWebsites, setRawWebsites] = useState([]);
  // Connection status is inferred CMS-side from user_generations (see
  // hooks/useWebsitesWithStatus). Same hook as the Websites tab, so the two
  // screens cannot disagree.
  // OLD: const websites = useWebsitesWithStatus(userData?.id, rawWebsites);
  //      (returned a bare array back when the server supplied last_connected_at
  //       and status needed no second query)
  const { websites, statusReady } = useWebsitesWithStatus(userData?.id, rawWebsites);
  const [websitesFetching, setWebsitesFetching] = useState(true);
  // Hold the list back until status is known too — otherwise every row paints
  // "Not Connected" for a beat and then flips, which reads as a bug.
  const websitesLoading = websitesFetching || !statusReady;
  // Adaptive Content Growth window: { anchor: Date|null, months: [{key,label,value}] }.
  // anchor === null means the user has never generated content (empty state).
  const [growth, setGrowth] = useState(EMPTY_GROWTH);
  // When the growth data was last read. Drives the "Updated …" line, which is
  // the card's answer to "how long does it take to sync?" — the user can see
  // how fresh the number is instead of guessing.
  const [growthUpdatedAt, setGrowthUpdatedAt] = useState(null);
  // Re-renders the relative timestamp as it ages, so "just now" does not sit
  // there claiming to be current ten minutes later.
  const [, setClockTick] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userData?.id) return;
    apiService.getUserWebsites(userData.id)
      .then(res => setRawWebsites(res?.data || []))
      .catch(() => setRawWebsites([]))
      .finally(() => setWebsitesFetching(false));
  }, [userData?.id]);

  // ── Content Growth, kept live ──────────────────────────────────────────────
  //
  // THE REPORTED BUG: "I generated a single post, it does not show up, how long
  // does it take to sync?" The answer used to be "until you reload the page" —
  // this ran once, inside the mount effect above, keyed on the user id. Nothing
  // ever re-read it, so a post generated after Overview was opened could never
  // appear, and the card gave no hint that what it showed was a snapshot.
  //
  // Articles are generated in the WordPress plugin, out-of-band, so there is no
  // event to subscribe to. Three triggers cover it:
  //   • on mount
  //   • every GROWTH_POLL_MS while the tab is visible
  //   • the moment the tab becomes visible again — the common path is "generate
  //     in WordPress, switch back to this tab", which now updates on arrival
  //     rather than up to a poll-interval later
  //
  // Every refresh is SILENT: it replaces the data in place and never drops back
  // to a spinner or the empty state, so a slow poll cannot make a populated
  // chart flicker.
  const loadGrowth = useCallback(async () => {
    if (!userData?.id) return;

    // ── ONE PATH ────────────────────────────────────────────────────────────
    // There used to be two: this aggregated call, and a fallback that fetched
    // raw generations and bucketed them client-side. BOTH were broken, and each
    // hid the other.
    //
    //   • the aggregated call queried a `credit_type` column that does not
    //     exist on user_generations, so it returned null on every poll;
    //   • the fallback then read `res.data.generations`, but the endpoint sends
    //     `data` as the array itself, so the chart got [] — the empty state, on
    //     an account with 126 articles.
    //
    // The fallback is gone rather than repaired. It called the SAME server the
    // primary had just failed against, so it was never real resilience; the
    // catch below is, because it leaves the last good chart on screen. Two
    // paths that must agree about what counts as "content" is one more thing to
    // get wrong, and it already was.
    try {
      const agg = await apiService.getContentGrowthAggregated(userData.id);
      // null means the request failed — keep what is on screen. An empty ARRAY
      // is a real answer (a new account) and must still be rendered, which is
      // why this tests for null rather than truthiness.
      if (agg === null) return;
      setGrowth(buildContentGrowthFromAggregated(agg));
      setGrowthUpdatedAt(new Date());
    } catch (_) {
      // Leave the last good data on screen. Blanking the chart because one poll
      // failed would look like the user's content had disappeared.
    }
  }, [userData?.id]);

  useEffect(() => {
    if (!userData?.id) return undefined;
    let cancelled = false;
    const run = () => { if (!cancelled) loadGrowth(); };

    run();
    const id = setInterval(() => {
      // Polling a hidden tab burns quota for a chart nobody is looking at; the
      // visibility handler below catches it up the instant they return.
      if (document.visibilityState === 'visible') run();
    }, GROWTH_POLL_MS);

    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userData?.id, loadGrowth]);

  // Ages the "Updated …" label independently of the fetch, so it stays honest
  // between polls. State-only; it triggers no network work.
  useEffect(() => {
    if (!growthUpdatedAt) return undefined;
    const id = setInterval(() => setClockTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [growthUpdatedAt]);

  // ── REMOVED: the "Quick Start Guide" 3-step card ───────────────────────────
  // The card was replaced by "My Websites" in the primary slot; the two
  // onboarding/support cards below take over the guidance job. Kept commented
  // out per the "don't delete replaced code" convention.
  //
  // const steps = [
  //   {
  //     key: 1,
  //     title: '1. Add Your Websites',
  //     desc: 'Connect your domains\nto start the analysis.',
  //     accent: C.accentOrange,
  //     circleBg: C.gradientOrange,
  //     cardBg: 'linear-gradient(145deg, #FFF5F0 0%, #FFE8DF 100%)',
  //     cardBorder: 'rgba(255,122,89,0.25)',
  //   },
  //   {
  //     key: 2,
  //     title: '2. Configure WordPress',
  //     desc: 'Install the plugin for\nseamless automation.',
  //     accent: C.accentTeal,
  //     circleBg: `linear-gradient(45deg, #0D4F4B 0%, ${C.accentTeal} 100%)`,
  //     cardBg: 'linear-gradient(145deg, #F0FBFA 0%, #C9EEEA 100%)',
  //     cardBorder: 'rgba(28,124,115,0.25)',
  //   },
  //   {
  //     key: 3,
  //     title: '3. Start Generating',
  //     desc: 'Create SEO-optimized\narticles in seconds.',
  //     accent: '#2DA4FD',
  //     circleBg: 'linear-gradient(45deg, #1266C2 0%, #2DA4FD 100%)',
  //     cardBg: 'linear-gradient(145deg, #F0F7FF 0%, #C8E1FC 100%)',
  //     cardBorder: 'rgba(45,164,253,0.25)',
  //   },
  // ];
  // ── END REMOVED ────────────────────────────────────────────────────────────

  // How many complete articles-with-image the current balance buys. The number
  // comes from GET /credits/estimate — see hooks/usePieceEstimate.js.
  const contentPiecesMessage = creditsSummaryMessage(totalCredits, contentPieces, pricing);

  // Overview shows a preview of the site list only; the Websites tab is the
  // full list. VIEW_ALL_THRESHOLD sites are shown before the "View All" button
  // becomes the way to see the rest.
  const OVERVIEW_SITE_PREVIEW = 3;
  const visibleWebsites = websites.slice(0, OVERVIEW_SITE_PREVIEW);
  const hasMoreWebsites = websites.length > OVERVIEW_SITE_PREVIEW;

  const goToQuickStart = () => navigate(QUICK_START_PATH);

  const maxVal = Math.max(1, ...growth.months.map(d => d.value));
  const growthLabel = growthLabelFor(growth.months.length);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>


      {/* Credits with a deadline, said the same way as the bell and the Credits
          page. It is the one thing on this page the user must act on within a
          something the user must act on, in a window that closes. Renders
          nothing when there is nothing expiring. */}
      <CreditExpiryBanner lots={lots} onViewCredits={() => onNavigate(CREDITS_PATH)} />

      {/* ── Hero Welcome ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: { xs: 28, md: 36 }, lineHeight: '40px', letterSpacing: '-0.025em', color: '#191C1D' }}>
          {/* First name only, and "Welcome back" only when there is something
              to come back to — see utils/greeting.js.
              OLD: Welcome back, {userData?.name || 'User'}. */}
          {greetingFor(userData, { returning })}
        </Typography>
        {/* The claim depends on there being a connected site — see
            utils/greeting.js. Held back until the websites request has
            resolved: both sentences are assertions about the account, and
            picking one before the answer is known means asserting something
            false for as long as the request takes. `&nbsp;` reserves the line
            so the hero does not jump when it arrives.
            OLD: an unconditional "…fine-tuned and ready.", shown to accounts
            with no website at all. */}
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 18, lineHeight: '28px', color: '#000814' }}>
          {websitesLoading
            ? ' '
            : heroSubtitle({ hasConnectedWebsite: websites.some(isWebsiteConnected) })}
        </Typography>
      </Box>

      {/* ── Bento Grid Main ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 280px' }, gap: 3 }}>

        {/* ── My Websites — promoted to the PRIMARY card ────────────────────
            Takes over the slot the "Quick Start Guide" 3-step card used to
            occupy. Each site shows its real connection status ("Active" once
            the plugin has verified the connection, otherwise "Not Connected"
            — see utils/websiteStatus.js) with a route into the Quick Start
            Guide for anything that still needs finishing. */}
        <Box sx={{ bgcolor: C.quickStartCard, borderRadius: '24px', p: '32px', boxShadow: '0px 8px 4px 0px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WebsiteIcon sx={{ color: C.accentOrange, fontSize: 22 }} />
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, color: '#191C1D' }}>My Websites</Typography>
            </Box>
            {!websitesLoading && websites.length > 0 && (
              <Box sx={{ bgcolor: 'rgba(255,122,89,0.2)', borderRadius: '9999px', px: 1.5, py: 0.5 }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 12, color: '#A7391E' }}>
                  {websites.length} {websites.length === 1 ? 'SITE' : 'SITES'}
                </Typography>
              </Box>
            )}
          </Box>

          {websitesLoading ? (
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: C.textMuted }}>Loading…</Typography>
          ) : websites.length === 0 ? (
            /* Empty state — one clear next action, into the Quick Start Guide */
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1.5, textAlign: 'center' }}>
              <WebsiteIcon sx={{ color: C.textMuted, fontSize: 44, opacity: 0.4 }} />
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 16, color: '#191C1D' }}>
                No websites yet
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: C.textMuted, maxWidth: 320, lineHeight: '18px' }}>
                Connect your first WordPress site to start generating SEO content.
              </Typography>
              <Button
                onClick={goToQuickStart}
                sx={{ background: C.gradientOrange, color: '#FFFFFF', borderRadius: '10px', fontWeight: 700, fontSize: 14, px: 3, py: 1.125, textTransform: 'none', mt: 1, boxShadow: '0px 4px 12px rgba(167,57,30,0.25)', '&:hover': { opacity: 0.9 } }}
              >
                {/* Client request — the label names the ACTION, not the page it
                    happens to open. This reverses the earlier "say where it
                    goes" rationale: on an empty My Websites card the user's
                    goal is connecting a site, and the sidebar already offers
                    "Quick Start Guide" by name for anyone who wants the guide.
                    OLD: "Quick Start Guide" */}
                Connect a website
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {visibleWebsites.map((site) => {
                  const connected = isWebsiteConnected(site);
                  return (
                    <Box key={site.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', bgcolor: '#F2F4F4', border: `1px solid ${C.websiteBorder}`, borderRadius: '16px', p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '8px', bgcolor: C.iconFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <WebsiteIcon sx={{ color: C.textMuted, fontSize: 20 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 15, color: '#0F172A', wordBreak: 'break-all' }}>
                            {site.domain.replace(/^https?:\/\//, '')}
                          </Typography>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 11, color: '#000814' }}>
                            Added {new Date(site.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        {/* Status: "Active" / "Not Connected" (was a hardcoded
                            ACTIVE/INACTIVE read of the soft-delete flag) */}
                        <Box sx={{ bgcolor: connected ? C.activeStatus.bg : C.syncStatus.bg, borderRadius: '9999px', px: 1.5, py: 0.5 }}>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap', color: connected ? C.activeStatus.text : C.syncStatus.text }}>
                            {websiteStatusLabel(site)}
                          </Typography>
                        </Box>
                        {!connected && (
                          <Button
                            size="small"
                            onClick={goToQuickStart}
                            sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 12, color: C.accentOrange, textTransform: 'none', px: 1, minWidth: 'auto', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,122,89,0.08)' } }}
                          >
                            Finish Setup
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  onClick={goToQuickStart}
                  sx={{ background: C.gradientOrange, color: '#FFFFFF', borderRadius: '10px', fontWeight: 700, fontSize: 13, px: 2.5, py: 1, textTransform: 'none', boxShadow: '0px 4px 12px rgba(167,57,30,0.25)', '&:hover': { opacity: 0.9 } }}
                >
                  {/* Client request. Kept identical to the empty-state button
                      above: same destination, same action, so the two must not
                      drift into different words.
                      OLD: "Quick Start Guide" */}
                  Connect a website
                </Button>
                {hasMoreWebsites && (
                  <Button
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                    onClick={() => onNavigate(WEBSITES_PATH)}
                    sx={{ fontWeight: 700, fontSize: 13, color: C.accentOrange, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,122,89,0.08)' } }}
                  >
                    View All Websites
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Right column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignSelf: 'stretch' }}>
          {/* Credits Card */}
          <Box sx={{ bgcolor: C.creditsCard, borderRadius: '24px', p: 4, boxShadow: '0px 8px 10px -6px rgba(0,0,0,0.1), 0px 20px 25px -5px rgba(0,0,0,0.1)', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
            <Box sx={{ position: 'absolute', right: -20, top: 60, width: 160, height: 160, borderRadius: '9999px', bgcolor: 'rgba(255,255,255,0.10)', filter: 'blur(64px)', pointerEvents: 'none' }} />
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 12, letterSpacing: '0.10em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>Available Credits</Typography>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 48, lineHeight: '48px', color: '#FFFFFF', mt: '20px', mb: '20px' }}>
              {totalCredits !== null ? totalCredits.toLocaleString() : '—'}
            </Typography>

            {/* ── REVERTED: per-type breakdown (Content / Images / Keywords) ──
                Back to a single "Available Credits" number on Overview per
                client feedback. The useCreditBalances hook is unchanged — we
                simply render `total` instead of the three-column row. The
                breakdown still feeds the value line below (it needs the
                per-type numbers to compute how many pieces are affordable).
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '20px', pb: '16px', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
              {Object.entries(CREDIT_TYPE_LABELS).map(([type, label]) => (
                <Box key={type} sx={{ textAlign: 'left' }}>
                  <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 20, lineHeight: '24px', color: '#FFFFFF' }}>
                    {creditsByType[type].toLocaleString()}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
                ── END REVERTED ─────────────────────────────────────────────── */}

            {/* Value line — what the balance is actually worth, computed from
                the live balances at 1 content + 1 image credit per piece. */}
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 13, lineHeight: '18px', color: 'rgba(255,255,255,0.75)', mb: '16px', pt: '16px', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
              {totalCredits === null
                ? 'Calculating what your balance is worth…'
                : contentPiecesMessage}
            </Typography>

            {/* Renamed: "Refill Credits" → "Add Credits" */}
            <Button fullWidth onClick={() => onNavigate(CREDITS_PATH)} sx={{ bgcolor: '#FFFFFF', color: C.creditsCard, borderRadius: '12px', fontWeight: 700, fontSize: 14, py: 1.5, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}>Add Credits</Button>
          </Box>
          {/* Avg Rank widget — removed from Overview per product request.
              Kept here (commented) for easy restoration; not gated by a flag
              since it was placeholder data, not a real feature toggle.
          <Box sx={{ bgcolor: C.white, borderRadius: '24px', p: 3, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.05)' }}>
            <Box sx={{ bgcolor: C.rankIcon, borderRadius: '16px', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <TrendingUpIcon sx={{ color: C.accentTeal, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', color: '#000814' }}>Avg. Rank Improvement</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 20, color: '#0F172A' }}>+12.4 Positions</Typography>
            </Box>
          </Box>
          */}
        </Box>
      </Box>

      {/* ── Bottom Row ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* ── Two helper cards, side by side, in the slot My Websites vacated ──
            Same card formatting as the rest of Overview. "Open tutorials" goes
            to Support, which is where the Knowledge Base and the Integration
            Tutorials live; "Submit a ticket" drops the user straight into the
            Support new-ticket flow. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
          {[
            {
              key: 'new-here',
              icon: PlayCircleIcon,
              title: 'New here?',
              body: 'Watch the 4-minute setup walkthrough.',
              cta: 'Open tutorials',
              // Client request. It used to open /tutorials, which renders the
              // SAME content as the Quick Start Guide tab — so the button
              // appeared to go nowhere new, which is what was reported. Support
              // is the page that actually holds the Knowledge Base.
              // OLD: navigate(PUBLIC_TUTORIALS_PATH)
              onClick: () => navigate(SUPPORT_PATH),
            },
            {
              key: 'need-a-hand',
              icon: TicketIcon,
              title: 'Need a hand?',
              body: 'Typical reply within one business day.',
              cta: 'Submit a ticket',
              // `newTicket` is picked up by SupportTickets, which opens the
              // new-ticket dialog on mount instead of just landing on the list.
              onClick: () => navigate(NEW_TICKET_PATH),
            },
          ].map(({ key, icon: CardIcon, title, body, cta, onClick }) => (
            <Box key={key} sx={{ bgcolor: C.warmCard, borderRadius: '24px', p: 4, boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CardIcon sx={{ color: C.accentOrange, fontSize: 22 }} />
                <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, color: '#191C1D' }}>{title}</Typography>
              </Box>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, lineHeight: '20px', color: '#000814', flex: 1 }}>
                {body}
              </Typography>
              <Button
                onClick={onClick}
                sx={{ alignSelf: 'flex-start', background: C.gradientOrange, color: '#FFFFFF', borderRadius: '10px', fontWeight: 700, fontSize: 13, px: 2.5, py: 1, textTransform: 'none', mt: 1, boxShadow: '0px 4px 12px rgba(167,57,30,0.25)', '&:hover': { opacity: 0.9 } }}
              >
                {cta}
              </Button>
            </Box>
          ))}
        </Box>

        {/* ── REPLACED: the old "My Websites" preview card that lived here ─────
            It has been promoted to the primary Overview card above (with real
            connection status, Finish Setup / View All / Connect actions).
            Kept commented out per the "don't delete replaced code" convention.

        <Box sx={{ bgcolor: C.warmCard, borderRadius: '24px', p: 4, boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, color: '#191C1D' }}>My Websites</Typography>
            <Button endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />} onClick={() => onNavigate(WEBSITES_PATH)} sx={{ fontWeight: 700, fontSize: 14, color: C.accentOrange, textTransform: 'none', p: 0, minWidth: 'auto', '&:hover': { bgcolor: 'transparent', opacity: 0.75 } }}>View All</Button>
          </Box>
          {websitesLoading ? (
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: C.textMuted }}>Loading…</Typography>
          ) : websites.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, gap: 1 }}>
              <WebsiteIcon sx={{ color: C.textMuted, fontSize: 40, opacity: 0.4 }} />
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: C.textMuted }}>No websites added yet</Typography>
              <Button size="small" onClick={() => onNavigate(WEBSITES_PATH)} sx={{ fontWeight: 700, fontSize: 13, color: C.accentOrange, textTransform: 'none', mt: 0.5 }}>Add your first website →</Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {websites.slice(0, 4).map((site) => (
                <Box key={site.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F2F4F4', border: `1px solid ${C.websiteBorder}`, borderRadius: '16px', p: 2 }}>
                  ...
                  <Box sx={{ bgcolor: site.active ? C.activeStatus.bg : C.syncStatus.bg, borderRadius: '9999px', px: 1.5, py: 0.5, flexShrink: 0 }}>
                    <Typography ...>{site.active ? 'ACTIVE' : 'INACTIVE'}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
            ── END REPLACED ─────────────────────────────────────────────────── */}

        {/* Content Growth Chart — adaptive window: grows from the user's first
            content generation up to a rolling 5-month view; empty state below
            when they've never generated content. */}
        <Box sx={{ bgcolor: C.warmCard, borderRadius: '24px', p: 4, boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, color: '#191C1D' }}>Content Growth</Typography>
              {/* The tooltip said "coming soon", which stopped being true once
                  this chart was reading real generations — and it was the only
                  explanation on the card, so a user whose post had not appeared
                  yet was told the feature did not exist. It now says what the
                  number counts and how quickly it catches up.
                  enterTouchDelay={0} so it also opens on tap, not just hover. */}
              <Tooltip
                title="Counts the articles generated through the ImproveSEO plugin, by month. Updates automatically about once a minute, and as soon as you return to this tab."
                arrow
                enterTouchDelay={0}
              >
                <IconButton size="small" aria-label="About Content Growth" sx={{ p: '2px', color: C.textMuted, '&:hover': { color: C.accentTeal, bgcolor: 'transparent' } }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {/* Hidden when there is no window to describe — see growthLabelFor. */}
              {growthLabel && (
                <Box sx={{ bgcolor: '#F2F4F4', borderRadius: '8px', px: 1.5, py: 0.5 }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 12, color: '#191C1D' }}>{growthLabel}</Typography>
                </Box>
              )}
              {/* The direct answer to "how long does it take to sync?": the user
                  never has to wait on the poll or reload the page. */}
              <Tooltip title="Check for new content now" arrow enterTouchDelay={0}>
                <IconButton
                  size="small"
                  aria-label="Refresh content growth"
                  onClick={loadGrowth}
                  sx={{ p: '4px', color: C.textMuted, '&:hover': { color: C.accentTeal, bgcolor: 'transparent' } }}
                >
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* ── Two different "nothing to show", said differently ──────────────
              `anchor === null` is "never generated anything". All-zero months
              with an anchor set is "has history, none of it recent" — which the
              fixed five-month window makes reachable for the first time, and
              which must NOT read as "you have never written an article" to a
              returning customer with hundreds of them. See utils/contentGrowth. */}
          {growth.anchor !== null && growth.months.every((m) => m.value === 0) ? (
            <Box sx={{ flex: 1, minHeight: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, textAlign: 'center', px: 2 }}>
              <Box sx={{ bgcolor: C.rankIcon, borderRadius: '9999px', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                <BarChartIcon sx={{ color: C.accentTeal, fontSize: 22 }} />
              </Box>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 16, color: '#191C1D' }}>
                Nothing published in the last {GROWTH_WINDOW_MONTHS} months
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: C.textMuted, maxWidth: 300, lineHeight: '18px' }}>
                Your earlier articles are still there — this chart only covers the
                last {GROWTH_WINDOW_MONTHS} months. Generate one from the plugin and it
                appears here within a minute.
              </Typography>
            </Box>
          ) : growth.anchor === null ? (
            /* ── Empty state: no content ever generated ── */
            <Box sx={{ flex: 1, minHeight: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, textAlign: 'center', px: 2 }}>
              <Box sx={{ bgcolor: C.rankIcon, borderRadius: '9999px', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                <BarChartIcon sx={{ color: C.accentTeal, fontSize: 22 }} />
              </Box>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 16, color: '#191C1D' }}>
                No content generated yet
              </Typography>
              {/* QA asked how long syncing takes, because the old sentence
                  ("Your monthly output shows up here once you publish your
                  first article.") described the end state without saying when
                  it arrives — leaving a user who had just generated a post
                  unable to tell "not synced yet" from "broken".
                  OLD: Your monthly output shows up here once you publish your first article. */}
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: C.textMuted, maxWidth: 280, lineHeight: '18px' }}>
                Generate an article from the ImproveSEO plugin on your site and your
                monthly output appears here, usually within a minute.
              </Typography>
              {/* Client feedback (Aug 8) — button REMOVED, not relabelled.
                  It read "Generate your first article" but its onClick was
                  goToQuickStart, so it opened the Quick Start Guide; content is
                  generated in the WordPress plugin, not here. There is no
                  in-CMS destination that would make the promise true, and the
                  Quick Start Guide is already one click away from the My
                  Websites card above, so the honest fix is to drop it and let
                  the sentence above explain the empty state.
                  OLD:
                  <Button onClick={goToQuickStart} sx={{ …gradientOrange… }}>
                    Generate your first article
                  </Button> */}
            </Box>
          ) : (
            /* ── The chart ────────────────────────────────────────────────────
                Five fixed columns. Three things the previous version got wrong,
                all visible in the same screenshot: 120 articles and 6 articles
                rendered as a full-height block beside a 6px sliver, with no
                number anywhere to tell them apart; months with no output drew
                nothing at all, so the axis had gaps where it should have had
                zeros; and the whole thing was invisible to a screen reader. */
            <Box
              role="img"
              aria-label={
                `Articles generated, last ${GROWTH_WINDOW_MONTHS} months: `
                + growth.months.map((m) => `${m.fullLabel}, ${m.value}`).join('; ')
              }
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: { xs: 1, sm: 2 },
                minHeight: GROWTH_PLOT_PX + 44,
                pt: 1,
                // A baseline gives the bars something to stand on, so a zero
                // month reads as "on the axis" rather than "missing".
                borderBottom: '1px solid rgba(25,28,29,0.10)',
              }}
            >
              {growth.months.map((d) => {
                const ratio = d.value / maxVal;
                // A month with output is never allowed to render shorter than
                // GROWTH_MIN_BAR_PX. At the real ratios above, August's 6 would
                // be 6px next to July's 120 — indistinguishable from nothing,
                // which is the opposite of what the number means.
                const height = d.value === 0
                  ? GROWTH_ZERO_BAR_PX
                  : Math.max(GROWTH_MIN_BAR_PX, Math.round(ratio * GROWTH_PLOT_PX));

                return (
                  <Tooltip
                    key={d.key}
                    arrow
                    enterTouchDelay={0}
                    title={
                      `${d.fullLabel}: ${d.value} ${d.value === 1 ? 'article' : 'articles'}`
                      + (d.isCurrent ? ' so far' : '')
                    }
                  >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      {/* The count, stated. A bar chart that makes the reader
                          estimate from height is asking them to guess. */}
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: d.value === 0 ? 500 : 700,
                          fontSize: 12,
                          lineHeight: 1,
                          color: d.value === 0 ? 'rgba(25,28,29,0.35)' : '#191C1D',
                        }}
                      >
                        {d.value.toLocaleString()}
                      </Typography>
                      <Box
                        sx={{
                          width: '100%',
                          height: `${height}px`,
                          borderRadius: d.value === 0 ? '2px' : '6px 6px 2px 2px',
                          // Zero months keep a faint stub so all five columns
                          // are present; drawing nothing looked like a gap in
                          // the data rather than a month with no output.
                          background: d.value === 0 ? 'rgba(25,28,29,0.10)' : C.gradientOrange,
                          // The current month is still being filled in. Softening
                          // it stops a part-month reading as a collapse in output.
                          opacity: d.value > 0 && d.isCurrent ? 0.75 : 1,
                          transition: 'height .25s ease',
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 11,
                          color: d.isCurrent ? '#191C1D' : C.textMuted,
                          fontWeight: d.isCurrent ? 700 : 500,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {d.label}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}

          {/* Freshness, stated rather than assumed. Without it the card is a
              snapshot with no timestamp, which is what made "it does not show
              up" impossible for the user to diagnose: they could not tell a
              stale reading from a missing article. */}
          {growthUpdatedAt && (
            <Typography
              aria-live="polite"
              sx={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: C.textMuted, textAlign: 'right', mt: -1.5 }}
            >
              Updated {relativeTime(growthUpdatedAt)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Analytics is hidden for now — flip this to restore the nav item + route below.
const SHOW_ANALYTICS = false;

// ── tab ↔ route pairing (every nav item is reachable at its own URL) ──────────
const TABS = [
  { label: 'Overview', icon: DashboardIcon, path: '/overview' },
  { label: 'Websites', icon: WebsiteIcon, path: '/websites' },
  { label: 'Credits & Subscriptions', icon: CreditIcon, path: '/credits' },
  // { label: 'Analytics', icon: AnalyticsIcon, path: '/analytics' },
  ...(SHOW_ANALYTICS ? [{ label: 'Analytics', icon: AnalyticsIcon, path: '/analytics' }] : []),
  { label: 'Support', icon: SupportIcon, path: '/support' },
  // Renamed: "Tutorial" → "Quick Start Guide" (nav label AND route).
  // OLD: { label: 'Tutorial', icon: TutorialIcon, path: '/tutorial' },
  { label: 'Quick Start Guide', icon: TutorialIcon, path: QUICK_START_PATH },
  { label: 'Settings', icon: SettingsIcon, path: '/settings' },
];

// ── Dashboard Shell ───────────────────────────────────────────────────────────
const Dashboard = () => {
  const { user, userData, signOut, refreshUserData } = useAuth();
  // Header bell. Lives on the shell so the count is visible from every tab.
  const { notifications, refresh: refreshNotifications } = useNotifications(userData?.id, { trialEndsAt: userData?.trial_ends_at });
  const location = useLocation();
  const navigate = useNavigate();

  // Active tab is derived from the URL so every tab is paired with a route
  // (e.g. /support, /tutorial). Unknown paths fall back to Overview.
  const matched = TABS.findIndex((t) => t.path === location.pathname);
  const activeTab = matched === -1 ? 0 : matched;

  // G1 — a hidden tab must not stay reachable by URL. Anything that isn't a
  // live tab (notably /analytics while SHOW_ANALYTICS is false, and the old
  // /tutorial path if the App-level redirect is ever removed) is normalised to
  // /overview, so the address bar can't claim a page that isn't rendering.
  // App.js matches its own routes first, so nothing public lands here.
  useEffect(() => {
    if (matched === -1) navigate('/overview', { replace: true });
  }, [matched, navigate]);

  // Path-based navigation handed to child components (OverviewTab,
  // CreditManagement → PlanSelector).
  //
  // OLD — index-based, and the direct cause of the "Add Payment Method goes to
  // the Support tab" bug. TABS is built conditionally (Analytics is spread in
  // only when SHOW_ANALYTICS is true), so hiding a tab shifted every later
  // index and silently repointed every hardcoded caller:
  //   const goToTab = (index) => navigate(TABS[index]?.path ?? '/overview');
  // Callers now pass a path from utils/routes.js, which is stable under
  // insertion, removal and reordering.
  const goToPath = (path) => navigate(path || OVERVIEW_PATH);

  // Header plan/trial badge. Paid plans show the plan's display name; free-plan
  // accounts show the trial state — "Free Trial" while active, "Trial ended" once
  // the window has elapsed (trial_expired from the server, with a local date fallback).
  const planSlug = (userData?.plan_slug || '').toLowerCase();
  const onFreePlan = !planSlug || planSlug === 'free';
  const trialExpired = userData?.trial_expired === true
    || (onFreePlan && !!userData?.trial_ends_at && new Date(userData.trial_ends_at).getTime() < Date.now());
  // "Basic plan" once the trial is over, not "Trial ended": the badge names the
  // plan for every paying account, so on a free account it has to name a plan
  // too. The account really is on Free — the downgrade is automatic — and
  // saying so keeps this badge, the Credits card and Settings on one answer.
  const planBadgeLabel = onFreePlan
    ? (trialExpired ? 'Basic plan' : 'Free Trial')
    : getPlanDisplayName({ name: userData?.plan_name, slug: userData?.plan_slug });

  // ── Stripe checkout return handling ─────────────────────────────────────────
  // After a redirect-based checkout (e.g. buy-credits) or card setup, Stripe sends
  // the user back with ?payment=success&session_id=... We verify the session so the
  // purchase is applied immediately (without waiting on the async webhook), then drop
  // the query params and land the user on the relevant tab.
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [notice, setNotice] = useState(null); // { severity, message }
  // The confirmation for a verified checkout — a credit pack, a new
  // subscription, or anything else Stripe hands back as paid. A modal, not the
  // `notice` toast: each of these is a purchase, and every other purchase and
  // plan change in the app confirms itself in the same dialog.
  //
  // OLD — credit packs only; everything else fell through to the toast:
  //   const [creditPurchaseSuccess, setCreditPurchaseSuccess] = useState(null); // { parts }
  const [checkoutSuccess, setCheckoutSuccess] = useState(null); // { title, parts, dismissed? }
  // Bumped after a verified payment to force the Credits tab to refetch balances,
  // since navigating to the same route does not remount it.
  const [creditsRefreshKey, setCreditsRefreshKey] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get('payment');
    const setup = params.get('setup');
    const sessionId = params.get('session_id');
    // Tells a credits-pack redirect apart from a subscription-checkout one —
    // see isCreditsPurchaseRedirect's doc comment for why this checks two
    // independent signals rather than trusting the URL alone.
    const isCreditsRedirect = isCreditsPurchaseRedirect(params);
    // Where the user was when they went off to add a card. Restricted to
    // in-app absolute paths: this value comes back from a Stripe redirect, so
    // treating it as a free-form URL would be an open redirect.
    const rawReturnTo = params.get('return_to');
    const returnTo = rawReturnTo && /^\/[A-Za-z0-9\-_/?=&]*$/.test(rawReturnTo) ? rawReturnTo : null;

    if (!payment && !setup) return;

    if (setup) {
      if (setup !== 'success') {
        // BILL-001 link audit — a cancelled card setup used to return the user
        // to Settings with no message at all. Silently landing back where you
        // started reads as "the button is broken", not "I changed my mind".
        setNotice({ severity: 'info', message: 'No card was added. You can try again whenever you like.' });
        navigate(returnTo || '/settings', { replace: true });
        return;
      }

      // BILL-001 — make the new card the default BEFORE letting the user act.
      //
      // This call used to be fire-and-forget, with an immediate navigate()
      // underneath it. That is root-cause hypothesis 3 from the ticket, and it
      // is real: a user who adds a card and clicks upgrade quickly can beat
      // their own promotion request, and gets told no default is set — about
      // the card they are looking at. Awaiting it costs one round trip at a
      // moment the user is already waiting on a redirect.
      //
      // A failure here is still not fatal: the server self-heals on the next
      // payment-methods read and on the upgrade itself. So the await is for
      // ordering, not for correctness, and the result is deliberately ignored.
      let active = true;
      (async () => {
        setVerifyingPayment(true);
        try {
          if (userData?.id) await apiService.ensureDefaultPaymentMethod(userData.id);
        } catch (e) {
          console.error('Could not confirm the default card:', e);
        } finally {
          if (!active) return;
          setVerifyingPayment(false);
          setNotice({ severity: 'success', message: 'Card saved. It will be used for your subscription.' });
          // Return the user to wherever they started, so adding a card mid
          // upgrade does not strand them in Settings having to find their way
          // back. Defaults to Settings, which is where the Add Card button on
          // that page belongs.
          navigate(returnTo || '/settings', { replace: true });
        }
      })();
      return () => { active = false; };
    }

    if (payment === 'cancelled') {
      setNotice({ severity: 'info', message: 'Payment cancelled — you were not charged.' });
      // Back to whatever the checkout was started from — the plan grid, for a
      // subscription checkout — so backing out of Stripe returns the user to
      // the choice they were making rather than to the credits summary.
      navigate(returnTo || '/credits', { replace: true });
      return;
    }

    if (payment === 'success') {
      let active = true;
      (async () => {
        let applied = false;
        let result = null;
        try {
          setVerifyingPayment(true);
          if (sessionId) {
            result = await apiService.verifyCheckoutSession(sessionId);
            // verify-session returns { success: false } when the payment hasn't
            // actually completed; only treat an explicit non-false as applied.
            applied = result?.success !== false;
          }
        } catch (e) {
          console.error('Payment verification failed:', e);
        } finally {
          if (!active) return;
          setVerifyingPayment(false);
          setCreditsRefreshKey((k) => k + 1);
          // A checkout can change the plan (e.g. trial → Grow), so re-fetch the user
          // record too. Without this the plan/trial badge above keeps its pre-checkout
          // value until the user reloads the page. This awaits, so re-check `active`
          // afterwards before touching state or navigating.
          try { await refreshUserData(); } catch (e) { console.error('User refresh failed:', e); }
          if (!active) return;
          // A credit-pack purchase gets the SAME "Credits added" modal a saved-card
          // purchase shows in-page (CreditManagement.handleCreditsPurchased) — this
          // used to fall through to the generic Snackbar below, which is exactly the
          // inconsistency QA reported: buying with a card already on file looked
          // different from buying when Checkout had to save the card first.
          if (applied && isCreditsRedirect) {
            setCheckoutSuccess({
              title: CREDIT_PURCHASE_CONFIRMED_TITLE,
              parts: creditPurchaseConfirmedMessage({
                credits: result?.data?.quantity,
                amountCents: result?.data?.amount,
                balanceAfter: result?.data?.balance_after,
                currency: result?.data?.currency,
                // Paid on Stripe's page, not on a card we already held.
                savedCard: false,
              }),
            });
          } else if (applied && result?.type === 'subscription') {
            // ── A subscription started on Stripe's page ─────────────────────
            // Trial → Grow with no card on file lands here. It used to get the
            // green "Payment successful!" toast, while every other plan change
            // confirms in the dialog — so it now gets the dialog too.
            //
            // The plan and renewal date are read back from the row the verify
            // call just activated rather than remembered from the card that was
            // clicked: if the two ever differ, the server is right. A failed
            // read still confirms — only without those two details.
            let subscription = null;
            try {
              if (userData?.id) {
                subscription = (await apiService.getUserSubscription(userData.id))?.data || null;
              }
            } catch (e) {
              console.error('Subscription refresh failed:', e);
            }
            if (!active) return;
            setCheckoutSuccess({
              title: SUBSCRIPTION_STARTED_TITLE,
              parts: subscriptionStartedMessage({
                planName: subscription?.plan_name,
                planSlug: subscription?.plan_slug,
                nextBillingDate: subscription?.next_billing_date,
              }),
            });
          } else if (applied) {
            // Anything else Stripe hands back as paid: the same dialog, never
            // the toast.
            setCheckoutSuccess({ title: PAYMENT_CONFIRMED_TITLE, parts: paymentConfirmedMessage() });
          } else {
            // OLD — a verified payment landed here too, as a toast:
            //   setNotice(applied
            //     ? { severity: 'success', message: 'Payment successful! Your account has been updated.' }
            //     : { severity: 'warning', message: … });
            setNotice({ severity: 'warning', message: 'Your payment went through, but we could not confirm the credits yet. They usually appear within a minute — refresh this page, and contact support if they don’t.' });
          }
          navigate('/credits', { replace: true });
        }
      })();
      return () => { active = false; };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSignOut = async () => {
    try { await signOut(); } catch (e) { console.error('Sign out error:', e); }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: C.pageBg }}>

      {/* ── Stripe checkout return: verifying overlay + result notice ── */}
      <Backdrop open={verifyingPayment} sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 1, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Confirming your payment…</Typography>
      </Backdrop>
      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {notice ? (
          <Alert onClose={() => setNotice(null)} severity={notice.severity} variant="filled" sx={{ borderRadius: '12px' }}>
            {notice.message}
          </Alert>
        ) : undefined}
      </Snackbar>
      {/* The one confirmation for every verified Stripe return — the same modal
          a saved-card purchase or an in-page plan change shows. The branches
          above decide its title and body.

          Dismissed rather than cleared on close, so the dialog keeps its title
          and body while it fades out; clearing it would flash the generic
          "Success" heading on the way out.

          OLD:
            open={!!creditPurchaseSuccess}
            onClose={() => setCreditPurchaseSuccess(null)}
            title={CREDIT_PURCHASE_CONFIRMED_TITLE}
            parts={creditPurchaseSuccess?.parts} */}
      <SuccessDialog
        open={Boolean(checkoutSuccess) && !checkoutSuccess.dismissed}
        onClose={() => setCheckoutSuccess((prev) => (prev ? { ...prev, dismissed: true } : prev))}
        title={checkoutSuccess?.title}
        parts={checkoutSuccess?.parts}
      />

      {/* ── Sidebar ── */}
      <Box sx={{ width: 256, position: 'fixed', top: 0, left: 0, height: '100vh', bgcolor: C.sidebarBg, borderRight: `1px solid ${C.sidebarBorder}`, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 3, zIndex: 1200, overflowY: 'auto' }}>
        <Box>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', overflow: 'hidden', flexShrink: 0, background: '#fff' }}>
              <img src="/improvelogo.png" alt="ImproveSEO" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 16, lineHeight: '20px', color: '#0F172A' }}>ImproveSEO</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 11, lineHeight: '14px', color: C.textMuted }}>Local SEO Content Engine</Typography>
            </Box>
          </Box>
          {/* Nav links */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {TABS.map((item, index) => {
              const Icon = item.icon;
              const active = activeTab === index;
              return (
                <Box key={index} onClick={() => navigate(item.path)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer', bgcolor: active ? '#FFFFFF' : 'transparent', boxShadow: active ? '0px 1px 2px 0px rgba(0,0,0,0.05)' : 'none', transition: 'background 0.15s', '&:hover': { bgcolor: active ? '#FFFFFF' : 'rgba(0,0,0,0.04)' } }}>
                  <Icon sx={{ fontSize: 18, color: active ? C.accentOrange : C.textMuted }} />
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: active ? 600 : 400, fontSize: 14, lineHeight: '20px', color: active ? C.text : C.textMuted }}>{item.label}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
        {/* Bottom */}
        <Box>
          {/* A1 — deep-link straight to the plans/subscriptions screen.
              OLD: navigate('/credits') landed on Credits & Usage, with no sign
              of where plans actually live. CreditManagement reads ?view=plans. */}
          <Button fullWidth onClick={() => navigate(PLANS_PATH)} sx={{ background: C.gradientOrange, color: '#FFFFFF', borderRadius: '12px', fontWeight: 700, fontSize: 14, py: 1.25, textTransform: 'none', mb: 2, boxShadow: '0px 4px 12px rgba(167,57,30,0.25)', '&:hover': { opacity: 0.9 } }}>Manage Plans</Button>
          <Divider sx={{ borderColor: C.sidebarBorder, mb: 1.5 }} />
          <Box onClick={() => navigate('/settings')} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
            <PersonIcon sx={{ fontSize: 18, color: C.textMuted }} />
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, color: C.textMuted }}>Profile</Typography>
          </Box>
          <Box onClick={handleSignOut} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
            <LogoutIcon sx={{ fontSize: 18, color: C.textMuted }} />
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, color: C.textMuted }}>Logout</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Main area ── */}
      <Box sx={{ ml: '256px', flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Sticky header. Height from APP_HEADER_HEIGHT, so the plan grid's
            scroll margin cannot drift from it. OLD: height: 64 */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, height: APP_HEADER_HEIGHT, bgcolor: C.headerBg, backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.sidebarBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 4 }}>
          {/* REMOVED — the search box. It was a bare InputBase with no onChange
              and no handler behind it: typing did nothing, which is what QA
              found. Rather than leave a control that lies about what the app can
              do, it is gone; the header now starts at the actions on the right.
              OLD:
              <Box sx={{ …pill… }}>
                <SearchIcon … />
                <InputBase placeholder="Search websites or metrics..." … />
              </Box> */}
          <Box />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* The bell used to be an icon with no click handler and no content.
                It now lists the conditions that need the user's attention and
                links each one to the place that resolves it — see
                utils/notifications.js. */}
            <NotificationsMenu
              notifications={notifications}
              onOpen={refreshNotifications}
              onNavigate={goToPath}
              textMuted={C.textMuted}
            />
            <Tooltip title="Settings"><IconButton size="small" onClick={() => navigate('/settings')}><SettingsIcon sx={{ color: C.textMuted, fontSize: 22 }} /></IconButton></Tooltip>
            <Divider orientation="vertical" flexItem sx={{ borderColor: C.sidebarBorder, mx: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, lineHeight: '20px', color: '#0F172A' }}>{userData?.name || user?.email}</Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 10, textTransform: 'uppercase', color: C.textMuted }}>{planBadgeLabel}</Typography>
              </Box>
              <Avatar sx={{ width: 40, height: 40, bgcolor: C.accentOrange, fontWeight: 700, fontSize: 14, border: '2px solid #FFFFFF', boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.05)' }}>{getInitials(userData?.name)}</Avatar>
            </Box>
          </Box>
        </Box>
        {/* Page content */}
        <Box sx={{ flex: 1, p: '48px', overflowY: 'auto' }}>
          {/* Matched by route path (not ordinal index) so hiding/restoring a tab
              in TABS above can never desync this block from the sidebar. */}
          {TABS[activeTab]?.path === '/overview' && <OverviewTab userData={userData} onNavigate={goToPath} />}
          {TABS[activeTab]?.path === '/websites' && <WebsiteManagement />}
          {TABS[activeTab]?.path === '/credits' && <CreditManagement key={creditsRefreshKey} onTabChange={goToPath} />}
          {SHOW_ANALYTICS && TABS[activeTab]?.path === '/analytics' && <AnalyticsDashboard />}
          {TABS[activeTab]?.path === '/support' && <SupportTickets />}
          {TABS[activeTab]?.path === QUICK_START_PATH && <Tutorial />}
          {TABS[activeTab]?.path === '/settings' && <Settings />}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;